import { ShieldCheck } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Seo } from "@/components/Seo";

// Change this date whenever the policy changes.
const LAST_UPDATED = "2 November 2026";

function Section({ title, children }) {
  return (
    <section className="mt-10">
      <h2 className="font-serif text-2xl font-semibold text-foreground">{title}</h2>
      <div className="mt-3 space-y-3 text-muted-foreground">{children}</div>
    </section>
  );
}

function List({ items }) {
  return (
    <ul className="list-disc space-y-1.5 pl-5">
      {items.map((t, i) => (
        <li key={i}>{t}</li>
      ))}
    </ul>
  );
}

export default function Privacy() {
  return (
    <div>
      <Seo
        title="Privacy Policy"
        description="How My Theatre collects, uses and protects your personal information, including your watchlist, diary, friends and email alerts."
      />
      <PageHero
        offset={4}
        overline="Your data, explained"
        title="Privacy Policy"
        callout={{
          icon: ShieldCheck,
          lead: "Short version.",
          text: "Your diary is private unless you choose otherwise. We never sell your data, and you decide who sees what.",
        }}
      />

      <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6">
        <p className="text-sm text-muted-foreground">Last updated: {LAST_UPDATED}</p>

        <Section title="Who we are">
          <p>
            My Theatre (mytheatre.uk) is a website that helps people find West End shows, follow
            cast changes, compare ticket sellers, and keep a personal theatre watchlist and diary.
            It is run by James Barham, who is the controller of the personal information described
            here. Questions about this policy or your data can be sent to{" "}
            <a className="underline" href="mailto:hello@mytheatre.uk">hello@mytheatre.uk</a>.
          </p>
        </Section>

        <Section title="What we collect">
          <p className="font-medium text-foreground">When you create an account</p>
          <List items={[
            "Your name and email address, and a password (stored only in scrambled, hashed form, never in plain text).",
            "Your email preferences, such as whether you want alerts.",
          ]} />

          <p className="pt-2 font-medium text-foreground">When you use My Theatre (watchlist and diary)</p>
          <List items={[
            "Shows you mark as seen or want to see, and the shows and performers you follow.",
            "Diary entries you write: the show, date, star rating, verdict, notes and review, best performance, who you would recommend it to, tags, and whether you would see it again.",
            "Private details you may add: who you went with, your seat area, the cast you saw, and your favourite moment. These are for you only and are never shown to anyone else.",
            "Shows that are not in our list, which you type in yourself (show name and venue). If you use “Suggest this show”, we store the name, the venue and how many different people have asked for it, so we can add it.",
            "Your trivia attempts and streaks, and your result in the West End role quiz.",
          ]} />

          <p className="pt-2 font-medium text-foreground">Lists and notes</p>
          <List items={[
            "Lists you create: a title, an optional description, the shows you pick and a one-line reason for each.",
            "Theatre notes you write: a title, plain text and, if you choose, a link to a show. Drafts are only visible to you.",
            "Optional profile details: a short bio, your favourite show, the first show you saw and a list you choose to pin. These are shown only to you and your accepted friends.",
            "Comments and replies you write on friends' lists and notes, and ones friends write on yours.",
            "Reactions friends give to your lists and notes, and reports other people send us about them or about comments (who reported, what they said and when).",
          ]} />

          <p className="pt-2 font-medium text-foreground">Friends and sharing</p>
          <List items={[
            "An optional username, if you choose one. It must be unique and appropriate, and we may refuse or remove usernames that are not.",
            "Friend requests you send and receive, your friend list, people you have blocked, and your personal invite link code.",
            "Reactions (heart, “seen it too”, “want to see”) that you give to, or receive on, diary entries, lists and notes.",
            "On-site notifications telling you about friend requests, acceptances and reactions.",
            "Whether you have chosen a username, which lets other signed-in members find you by it.",
          ]} />

          <p className="pt-2 font-medium text-foreground">Automatically</p>
          <List items={[
            "A secure sign-in cookie that keeps you logged in. It is essential for the site to work.",
            "Small preferences saved in your own browser, such as light or dark mode and the last My Theatre tab you opened. These stay on your device.",
            "Standard technical logs kept by our hosting provider, such as IP address and browser type, used for security and to keep the site running.",
            "If you click a link to a ticket seller, the seller or its affiliate network may set its own cookies on their site. See “Ticket links and affiliate links” below.",
          ]} />
        </Section>

        <Section title="Who can see your diary">
          <p>
            Every diary entry has one of three settings, and the default is{" "}
            <strong className="text-foreground">Only me</strong>.
          </p>
          <List items={[
            "Only me: nobody else can see it.",
            "My friends: people you have accepted as friends can see that the entry exists, with your rating and date.",
            "Anyone with my link: anyone who has your page address can see it, whether or not they have an account. This only works if you have chosen a username and switched your public page on.",
          ]} />
          <p>
            For shared entries, your written thoughts (notes, verdict, review, best performance,
            recommendations and tags) are only included if you tick “Include my thoughts”. Your
            seat, who you went with, the cast you saw and your favourite moment are never shared.
          </p>
          <p>
            Your public page asks search engines not to list it, but anyone with the link can pass
            it on, so only share entries you are comfortable with being seen. You can change an
            entry to a more private setting, or switch your public page off, at any time, and it
            takes effect straight away.
          </p>
        </Section>

        <Section title="Lists and notes">
          <p>
            Each list is <strong className="text-foreground">Only me</strong>, <strong className="text-foreground">My friends</strong> or{" "}
            <strong className="text-foreground">Anyone with the link</strong>, and starts as Only me. Anyone with the link of a public
            list can read it, whether or not they have an account, and it shows your username. Lists ask search engines not to list them.
          </p>
          <p>
            Theatre notes are never public. A note is either a draft that only you can see, or visible to people you have accepted as friends.
            Notes are plain text and links in them are not clickable. Only post things you are happy for your friends to read, and please do
            not post personal information about other people.
          </p>
          <p>
            Friends can comment on your lists and notes, and you can reply. Comments can only be read by you and your accepted friends, never by
            the public, and you can turn comments off for any list or note. You can delete any comment on your own content, and the person
            who wrote a comment can edit or delete it. If you unfriend or block someone, their comments on your content, and yours on theirs, are removed.
          </p>
          <p>
            Anyone who can see a list, note, comment or profile can report it to us. We may hide content that breaks our rules, or that is reported
            and found to be abusive, and we may switch off a user's sharing. Hidden content stays visible to its author.
          </p>
        </Section>

        <Section title="Finding people">
        <p>
            If you choose a username, other signed-in members can find you by it. They can search for the start of your username (at least three letters) and will see only your username,
            along with an “Add as friend” button. Your bio, diary, lists and notes are not shown in search results. Nobody can browse a list of members. You still choose whether to accept any request, and you can
            block someone or remove a friend at any time. If you don't want to be found, don't choose a username, or ask us to remove it.
          </p>
          <p>
            To stop searches being used to collect usernames, we keep a record that a search happened (who and when, not what was searched for), limit how
            many searches each person can make per hour, and delete these records automatically after about a day.
          </p>
        </Section>

        <Section title="Friends">
          <p>
            Adding a friend needs two people: you send a request and they must accept it. A person
            you have not accepted cannot see your friends-only entries. If someone declines your
            request, you are not told. If you block someone, they cannot find your page, send you
            requests or react to your entries, and any reactions between the two of you are removed.
            You can unfriend or unblock a person whenever you like.
          </p>
          <p>
            Your invite link lets someone send you a friend request. It does not give them access to
            your diary. You can reset the link to stop the old one working.
          </p>
          <p>
            If you use the “invite by email” option, it opens your own email app with a message
            ready to send. We do not send that message and we do not see or store the address you
            type in. Only invite people you know.
          </p>
        </Section>

        <Section title="How we use your information">
          <List items={[
            "To run your account and the features you ask for: watchlist, diary, lists, notes, friends, trivia and the role quiz.",
            "To send emails you have asked for, such as cast-change alerts and closing-soon alerts for the shows you follow.",
            "To send a one-off welcome email when you sign up, and service emails such as password resets.",
            "To keep the site safe, prevent misuse and respond to reports.",
            "To improve the site, for example by deciding which unlisted shows to add.",
          ]} />
          <p>
            Under UK data protection law we rely on: performing our agreement with you (running your
            account and features); your consent (optional sharing, alerts and your username); and our
            legitimate interests (security, preventing abuse and improving the site).
          </p>
          <p>
            We do not sell your personal information, and we do not use your diary to show you
            advertising.
          </p>
        </Section>

        <Section title="Emails">
          <p>
            Alert emails are sent only if you follow a show or performer and have alerts switched
            on. Every email contains an unsubscribe link, and you can also change this in your
            account. The welcome email is sent once when you sign up. Friend activity is shown on the
            site; we do not currently email you about it.
          </p>
        </Section>

        <Section title="Ticket links and affiliate links">
          <p>
            Some links to ticket sellers on My Theatre are affiliate links. If you click one and then
            book, the seller may pay us a small commission. It does not change the price you pay, and
            where a link is an affiliate link we say so near it.
          </p>
          <p>
            When you click an affiliate link, the seller or its affiliate network (for example Awin)
            may set cookies on their own site to record that you came from My Theatre, so the booking
            can be credited to us. We do not control those cookies and we do not add them to My Theatre's
            own pages. We do not give the seller or network your name, email address, diary or any
            other account details. Their own privacy and cookie policies apply once you leave our site.
          </p>
        </Section>

        <Section title="Who we share data with">
          <p>We only use trusted services needed to run the site, which process data on our behalf:</p>
          <List items={[
            "Our web hosting provider (Render), which runs the website and servers.",
            "Our database provider, which stores account and diary data.",
            "Resend, which delivers our emails.",
          ]} />
          <p>
            Ticket sellers and affiliate networks only receive anything when you choose to click
            through to them, as described above.
          </p>
          <p>
            Some of these providers are based outside the UK, so data may be transferred
            internationally under appropriate safeguards. We may also disclose information if the
            law requires it, or to protect people from harm.
          </p>
          <p>
            Links to ticket sellers and other sites take you away from My Theatre. Their own privacy
            policies apply there.
          </p>
        </Section>

        <Section title="Keeping an eye on content">
          <p>
            Diary text is written by users. If you see something on a public page that is abusive or
            should not be there, tell us at{" "}
            <a className="underline" href="mailto:hello@mytheatre.uk">hello@mytheatre.uk</a> or use
            the report link on the page. We can switch off a public page or remove a username if it
            breaks our rules.
          </p>
        </Section>

        <Section title="How long we keep it">
          <p>
            We keep your information while your account is open. Unaccepted friend requests expire
            after 30 days. If you ask us to delete your account, we will remove your account and
            diary, along with your friend links, reactions and notifications. Some records, such as
            ordinary server logs or backups, may remain for a short period before they are removed.
          </p>
        </Section>

        <Section title="Your rights">
          <p>Under UK data protection law you can ask us to:</p>
          <List items={[
            "Show you the personal information we hold about you.",
            "Correct anything that is wrong.",
            "Delete your information.",
            "Restrict or object to how we use it, or give you a copy in a portable format.",
            "Withdraw consent at any time, for example by unsubscribing or turning your public page off.",
          ]} />
          <p>
            Email{" "}
            <a className="underline" href="mailto:hello@mytheatre.uk">hello@mytheatre.uk</a> and we
            will reply within one month. If you are unhappy with how we have handled your data, you
            can complain to the Information Commissioner’s Office at ico.org.uk or on 0303 123 1113.
          </p>
        </Section>

        <Section title="Children">
          <p>
            My Theatre is not aimed at children, and you must be 13 or over to create an account.
            Please do not use the friends or sharing features if you are under 18 without a parent
            or carer knowing.
          </p>
        </Section>

        <Section title="Changes to this policy">
          <p>
            If we change how we use your information, for example by adding a new feature, we will
            update this page and the date at the top. For significant changes we will let account
            holders know.
          </p>
        </Section>
      </div>
    </div>
  );
}